import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import SOCCarousel from '@/components/SOC/SOCCarousel';
import useAutoRefresh from '@/hooks/useAutoRefresh';
import '@/styles/ITReportStyles.scss';
import '@/styles/ThreatHuntingStyles.scss';
import '@/styles/SOCDashboard.scss';
import * as Sentry from '@sentry/react';

/**
 * Threat Hunting Report Component
 * Displays Microsoft Defender Advanced Threat Hunting results for DLP and Insider Risk detection
 * Now includes SOC Dashboard mode for 24/7 monitoring
 */
const ThreatHuntingReport = () => {
  const { apiFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [previousData, setPreviousData] = useState(null); // Store previous data for trend calculation
  const [daysBack, setDaysBack] = useState(30);
  const [enhancedMode, setEnhancedMode] = useState(false); // Default to false - users opt-in after Graph API permissions configured
  const [socMode, setSocMode] = useState(false); // SOC Dashboard mode toggle

  useEffect(() => {
    fetchThreatHuntingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysBack, enhancedMode]);

  const fetchThreatHuntingData = async () => {
    setLoading(true);
    setError(null);

    try {
      const summaryEndpoint = enhancedMode
        ? `/api/ThreatHunting/enhanced-summary?daysBack=${daysBack}`
        : `/api/ThreatHunting/summary?daysBack=${daysBack}`;

      const reportEndpoint = enhancedMode
        ? `/api/ThreatHunting/enhanced-report?daysBack=${daysBack}`
        : `/api/ThreatHunting/report?daysBack=${daysBack}`;

      const [summaryResp, reportResp] = await Promise.all([
        apiFetch(summaryEndpoint),
        apiFetch(reportEndpoint),
      ]);

      // Parse JSON bodies
      const [summaryJson, reportJson] = await Promise.all([
        summaryResp.json().catch(() => null),
        reportResp.json().catch(() => null),
      ]);

      // If either response is not ok, surface an error message from the body if possible
      if (!summaryResp.ok) {
        const msg = (summaryJson && (summaryJson.error || summaryJson.message)) || `Failed to load summary (HTTP ${summaryResp.status})`;
        throw new Error(msg);
      }
      if (!reportResp.ok) {
        const msg = (reportJson && (reportJson.error || reportJson.message)) || `Failed to load report (HTTP ${reportResp.status})`;
        throw new Error(msg);
      }

      setSummary(summaryJson);
      setReportData(reportJson);

      // Store previous data for trend calculation in SOC mode
      if (summary && reportData) {
        setPreviousData({ summary, reportData });
      }
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error fetching threat hunting data:', err);
      setError(err?.message || 'Failed to load threat hunting data');
    } finally {
      setLoading(false);
    }
  };

  // Set up auto-refresh for SOC mode (2-minute interval)
  const autoRefresh = useAutoRefresh(
    fetchThreatHuntingData,
    2, // 2 minutes interval
    !socMode // Initially paused if not in SOC mode
  );

  // Auto-enable enhanced mode when entering SOC mode
  useEffect(() => {
    if (socMode && !enhancedMode) {
      setEnhancedMode(true);
    }
  }, [socMode, enhancedMode]);

  // Pause auto-refresh when not in SOC mode
  useEffect(() => {
    if (!socMode && !autoRefresh.isPaused) {
      autoRefresh.pause();
    } else if (socMode && autoRefresh.isPaused) {
      autoRefresh.resume();
    }
  }, [socMode, autoRefresh]);

  const refreshData = async () => {
    try {
      setLoading(true); // Set loading state for user feedback

      // Store current data as previous before refresh
      if (summary && reportData) {
        setPreviousData({ summary, reportData });
      }

      // Clear cache and refetch using apiFetch
      const clearResp = await apiFetch('/api/ThreatHunting/clear-cache', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });

      if (!clearResp.ok) {
        const errBody = await clearResp.json().catch(() => null);
        throw new Error((errBody && (errBody.error || errBody.message)) || `Failed to clear cache (HTTP ${clearResp.status})`);
      }

      await fetchThreatHuntingData();
    } catch (err) {
        Sentry.captureException(err);
      console.error('Error refreshing data:', err);
      setError(err?.message || 'Failed to refresh data');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const renderGraphAPIResults = (categoryName, data) => {
    if (!data || data.error) {
      return (
        <div className="query-result-section graph-api-result">
          <h4>{categoryName}</h4>
          <div className="result-count">
            <span className="no-incidents">No data available</span>
          </div>
          {data?.message && (
            <div className="info-message">
              <p>{data.message}</p>
              {data.recommendation && <p className="recommendation">{data.recommendation}</p>}
            </div>
          )}
        </div>
      );
    }

    const count = data.count || 0;
    const items = data.devices || data.users || data.signIns || data.anomalousLogons || [];

    return (
      <div className="query-result-section graph-api-result">
        <h4>{categoryName}</h4>
        <div className="result-count">
          {count > 0 ? (
            <span className="alert-badge">{count} item{count !== 1 ? 's' : ''} found</span>
          ) : (
            <span className="no-incidents">No issues detected</span>
          )}
        </div>
        {count > 0 && items.length > 0 && (
          <div className="results-preview">
            <div className="graph-api-items">
              {items.slice(0, 3).map((item, idx) => (
                <div key={idx} className="graph-api-item">
                  <p><strong>{item.deviceName || item.userPrincipalName || item.user || 'Unknown'}</strong></p>
                  {item.threatLevel && <p className="threat-level">Threat: {item.threatLevel}</p>}
                  {item.riskLevel && <p className="risk-level">Risk: {item.riskLevel}</p>}
                  {item.complianceState && <p className="compliance-state">Status: {item.complianceState}</p>}
                  {item.failedAttempts && <p className="failed-attempts">Failed attempts: {item.failedAttempts}</p>}
                </div>
              ))}
              {items.length > 3 && (
                <p className="more-items">... and {items.length - 3} more</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderQueryResults = (queryName, data) => {
    if (!data || data.error) {
      return (
        <div className="query-result-error">
          <h4>{queryName}</h4>
          <p className="error-message">Error: {data?.error || 'No data available'}</p>
        </div>
      );
    }

    // Parse the results from the Graph API response
    // Note: The actual structure will depend on the API response format
    let results = [];
    try {
      if (data.results) {
        results = Array.isArray(data.results) ? data.results : [];
      } else if (Array.isArray(data)) {
        results = data;
      }
    } catch (e) {
        Sentry.captureException(e);
      console.error('Error parsing query results:', e);
    }

    return (
      <div className="query-result-section">
        <h4>{queryName}</h4>
        <div className="result-count">
          {results.length > 0 ? (
            <span className="alert-badge">{results.length} incident{results.length !== 1 ? 's' : ''} detected</span>
          ) : (
            <span className="no-incidents">No incidents detected</span>
          )}
        </div>
        {results.length > 0 && (
          <div className="results-preview">
            <p className="preview-note">
              Advanced hunting query returned {results.length} result{results.length !== 1 ? 's' : ''}. 
              Review details in Microsoft 365 Defender portal for full context.
            </p>
          </div>
        )}
      </div>
    );
  };

  if (loading && !socMode) {
    return <HexagonLoader />;
  }

  if (error && !socMode) {
    return (
      <div className="main-content" style={{ padding: '20px' }}>
        <div className="error-container">
          <h2>Error Loading Threat Hunting Report</h2>
          <p className="error-message">{error}</p>
          <button onClick={refreshData} className="btn-refresh">Try Again</button>
        </div>
      </div>
    );
  }

  // If SOC mode is enabled, render the SOC Dashboard
  if (socMode) {
    return (
      <SOCCarousel
        data={summary}
        previousData={previousData?.summary}
        onRefresh={refreshData}
        onExit={() => setSocMode(false)}
        cycleInterval={15}
        connectionStatus={autoRefresh.connectionStatus}
        countdownDisplay={autoRefresh.countdownDisplay}
        lastRefreshTime={autoRefresh.lastRefreshTime}
        autoRefreshPaused={autoRefresh.isPaused}
        toggleAutoRefreshPause={autoRefresh.togglePause}
      />
    );
  }

  // Standard threat hunting report view
  return (
    <div className="main-content" style={{ padding: '20px' }}>
      <div className="threat-hunting-header">
        <h1>Defender Advanced Threat Hunting Report</h1>
        <p className="subtitle">DLP & Insider Risk Detection</p>
        
        <div className="controls-bar">
          <div className="soc-mode-toggle">
            <button 
              onClick={() => setSocMode(true)}
              className="btn-soc-mode"
              title="Switch to SOC Dashboard mode for 24/7 monitoring"
            >
              🖥️ SOC Dashboard Mode
            </button>
          </div>

          <div className="time-range-selector">
            <label htmlFor="daysBack">Time Range:</label>
            <select 
              id="daysBack" 
              value={daysBack} 
              onChange={(e) => setDaysBack(parseInt(e.target.value))}
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          <div className="enhanced-mode-toggle">
            <label htmlFor="enhancedMode">
              <input 
                type="checkbox" 
                id="enhancedMode"
                checked={enhancedMode}
                onChange={(e) => setEnhancedMode(e.target.checked)}
              />
              Enhanced Mode (Graph API)
            </label>
          </div>
          
          <button onClick={refreshData} className="btn-refresh">
            Refresh Data
          </button>
        </div>
      </div>

      {summary && (
        <div className="summary-section">
          <div className="summary-cards">
            <div className="summary-card dlp-card">
              <h3>DLP Incidents</h3>
              <div className="summary-value">{summary.totalDLPIncidents || 0}</div>
              <p>Data Loss Prevention incidents detected</p>
            </div>
            
            <div className="summary-card insider-risk-card">
              <h3>Insider Risk Incidents</h3>
              <div className="summary-value">{summary.totalInsiderRiskIncidents || 0}</div>
              <p>Insider risk indicators detected</p>
            </div>
            
            {enhancedMode && (
              <>
                <div className="summary-card intune-card">
                  <h3>Intune Alerts</h3>
                  <div className="summary-value">
                    {(summary.intuneNonCompliantDeviceCount || 0) + (summary.intuneThreatenedDeviceCount || 0)}
                  </div>
                  <p>Non-compliant & threatened devices</p>
                </div>
                
                <div className="summary-card entra-card">
                  <h3>Entra ID Risks</h3>
                  <div className="summary-value">
                    {(summary.entraRiskyUserCount || 0) + (summary.entraRiskySignInCount || 0)}
                  </div>
                  <p>Risky users & sign-ins detected</p>
                </div>
              </>
            )}
            
            <div className="summary-card info-card">
              <h3>Report Generated</h3>
              <div className="summary-value-small">{formatDate(summary.reportGeneratedAt)}</div>
              <p>Time range: {daysBack} days{enhancedMode && ' (Enhanced)'}</p>
            </div>
          </div>
        </div>
      )}

      {reportData && (
        <div className="threat-hunting-results">
          <section className="dlp-section">
            <h2>Data Loss Prevention (DLP)</h2>
            <div className="query-results-grid">
              {renderQueryResults('File Exfiltration Detection', reportData.dlpFileExfiltration)}
              {renderQueryResults('Unusual Sharing Patterns', reportData.dlpUnusualSharing)}
              {renderQueryResults('Large Data Transfers', reportData.dlpLargeDataTransfers)}
            </div>
          </section>

          <section className="insider-risk-section">
            <h2>Insider Risk Indicators</h2>
            <div className="query-results-grid">
              {renderQueryResults('Anomalous Access Patterns', reportData.insiderRiskAnomalousAccess)}
              {renderQueryResults('Privilege Escalation Attempts', reportData.insiderRiskPrivilegeEscalation)}
              {renderQueryResults('Off-Hours Activity', reportData.insiderRiskOffHoursActivity)}
              {renderQueryResults('Suspicious Downloads', reportData.insiderRiskSuspiciousDownloads)}
            </div>
          </section>

          {enhancedMode && reportData.enhancedReport && (
            <>
              <section className="graph-api-section intune-section">
                <h2>Intune Device Security</h2>
                <div className="query-results-grid">
                  {renderGraphAPIResults('Non-Compliant Devices', reportData.intuneNonCompliantDevices)}
                  {renderGraphAPIResults('Devices with Threats', reportData.intuneDevicesWithThreatDetections)}
                </div>
              </section>

              <section className="graph-api-section entra-section">
                <h2>Entra ID (Azure AD) Security</h2>
                <div className="query-results-grid">
                  {renderGraphAPIResults('Risky Users', reportData.entraRiskyUsers)}
                  {renderGraphAPIResults('Risky Sign-Ins', reportData.entraRiskySignIns)}
                  {renderGraphAPIResults('Anomalous Logons', reportData.entraAnomalousLogons)}
                </div>
              </section>

              <section className="graph-api-section correlation-section">
                <h2>Cross-Platform Correlation</h2>
                <div className="correlation-info">
                  {reportData.crossPlatformCorrelations && (
                    <div className="correlation-details">
                      <p><strong>Data Sources:</strong> {reportData.dataSources?.join(', ') || 'Multiple sources'}</p>
                      <p><strong>Correlation Engine:</strong> {reportData.correlationEngine || 'Enabled'}</p>
                      {reportData.crossPlatformCorrelations.patterns && (
                        <div className="correlation-patterns">
                          <h4>Monitored Patterns:</h4>
                          <ul>
                            {reportData.crossPlatformCorrelations.patterns.map((pattern, idx) => (
                              <li key={idx}>{pattern}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>
            </>
          )}

          <section className="report-metadata">
            <h3>Report Information</h3>
            <div className="metadata-grid">
              <div className="metadata-item">
                <strong>Queries Executed:</strong> {reportData.queryCount || 'N/A'}
              </div>
              <div className="metadata-item">
                <strong>Time Range:</strong> {reportData.timeRange ? 
                  `${new Date(reportData.timeRange.startDate).toLocaleDateString()} - ${new Date(reportData.timeRange.endDate).toLocaleDateString()}` 
                  : 'N/A'}
              </div>
              <div className="metadata-item">
                <strong>Generated At:</strong> {formatDate(reportData.generatedAt)}
              </div>
            </div>
          </section>

          <div className="report-footer">
            <p className="disclaimer">
              <strong>Note:</strong> This report uses Microsoft Defender Advanced Threat Hunting queries to identify 
              potential security incidents. Results are based on telemetry from Microsoft 365 Defender. 
              Always correlate findings with additional context and investigate alerts through the Microsoft 365 Defender portal.
            </p>
            <p className="api-info">
              Data source: Microsoft Graph Security API - Advanced Hunting
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThreatHuntingReport;
